import type { ExternalProofSalesRoomClientSafeViewV1 } from "@enrein/shared-contracts";
import type { ClientPortalApiClient } from "./api.js";

export interface ExternalProofSalesRoomPanelProps {
  readonly view: ExternalProofSalesRoomClientSafeViewV1;
  readonly canAct: boolean;
  readonly changeMessage: string;
  readonly setChangeMessage: (value: string) => void;
  readonly onApprove: () => void;
  readonly onRequestChange: () => void;
  readonly actionStatus: string | null;
}

function sectionVisible(
  view: ExternalProofSalesRoomClientSafeViewV1,
  section: ExternalProofSalesRoomClientSafeViewV1["visibleSections"][number],
): boolean {
  return view.visibleSections.includes(section);
}

export function ExternalProofSalesRoomPanel({
  view,
  canAct,
  changeMessage,
  setChangeMessage,
  onApprove,
  onRequestChange,
  actionStatus,
}: ExternalProofSalesRoomPanelProps) {
  return (
    <section className="panel panel--external-proof value-card" aria-label="External proof review">
      <p className="value-card-eyebrow">External website proof</p>
      <h2 className="value-card-headline">Review findings and recommended fixes</h2>
      <p className="journey-subtitle">
        Your agency prepared this client-safe summary from confirmed scan and readiness evidence. Enrein does
        not edit your external website automatically.
      </p>

      {sectionVisible(view, "siteSummary") && view.siteSummary ? (
        <article className="external-proof-block">
          <h3>Current site</h3>
          <p>{view.siteSummary.headline}</p>
          {view.siteSummary.checkHighlights?.length ? (
            <ul className="external-proof-list">
              {view.siteSummary.checkHighlights.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
        </article>
      ) : null}

      {sectionVisible(view, "readinessSummary") && view.readinessHeadline ? (
        <article className="external-proof-block">
          <h3>Readiness summary</h3>
          <p>{view.readinessHeadline}</p>
          {view.readinessFindings?.length ? (
            <ul className="external-proof-list">
              {view.readinessFindings.map((finding) => (
                <li key={`${finding.categoryLabel}-${finding.summary}`}>
                  <strong>{finding.categoryLabel}</strong> — {finding.summary}
                </li>
              ))}
            </ul>
          ) : null}
        </article>
      ) : null}

      {sectionVisible(view, "topIssues") && view.topIssues?.length ? (
        <article className="external-proof-block">
          <h3>Top issues</h3>
          <ul className="external-proof-list">
            {view.topIssues.map((issue) => (
              <li key={issue.summary}>
                <strong>{issue.severity}</strong> — {issue.summary}
                <p>{issue.recommendation}</p>
              </li>
            ))}
          </ul>
        </article>
      ) : null}

      {sectionVisible(view, "fixKitSummary") && view.fixKitExecutiveSummary ? (
        <article className="external-proof-block">
          <h3>Recommended fixes</h3>
          <p>{view.fixKitExecutiveSummary}</p>
          {view.fixKitItems?.length ? (
            <ul className="external-proof-list">
              {view.fixKitItems.map((item) => (
                <li key={item.issueSummary}>
                  <strong>{item.issueSummary}</strong>
                  <p>{item.clientSafeExplanation}</p>
                </li>
              ))}
            </ul>
          ) : null}
        </article>
      ) : null}

      {sectionVisible(view, "agencyRecommendation") && view.agencyRecommendation ? (
        <article className="external-proof-block">
          <h3>What your agency recommends</h3>
          <p>{view.agencyRecommendation}</p>
        </article>
      ) : null}

      {sectionVisible(view, "clientActionItems") && view.clientRequestedItems?.length ? (
        <article className="external-proof-block">
          <h3>What we need from you</h3>
          <ul className="external-proof-list">
            {view.clientRequestedItems.map((item) => (
              <li key={item.label}>
                <strong>{item.label}</strong>
                <p>{item.detail}</p>
              </li>
            ))}
          </ul>
        </article>
      ) : null}

      {sectionVisible(view, "blockedItems") && view.blockedItems?.length ? (
        <article className="external-proof-block">
          <h3>Blocked or needs proof</h3>
          <ul className="external-proof-list">
            {view.blockedItems.map((item) => (
              <li key={item.label}>
                <strong>{item.label}</strong>
                <p>{item.detail}</p>
              </li>
            ))}
          </ul>
        </article>
      ) : null}

      {sectionVisible(view, "handoffPlaceholder") && view.handoffPlaceholder ? (
        <article className="external-proof-block">
          <h3>Handoff</h3>
          <p>{view.handoffPlaceholder.summary}</p>
        </article>
      ) : null}

      <details className="external-proof-disclaimers">
        <summary>What we do not promise</summary>
        <ul className="external-proof-list">
          {view.whatWeDoNotPromise.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </details>

      {canAct ? (
        <div className="action-row external-proof-actions">
          <button type="button" className="btn-primary" onClick={onApprove}>
            Approve review plan
          </button>
          <label className="field-label" htmlFor="external-proof-change-message">
            Request changes
          </label>
          <textarea
            id="external-proof-change-message"
            value={changeMessage}
            onChange={(event) => setChangeMessage(event.target.value)}
            placeholder="Tell your agency what you would like clarified or changed."
          />
          <button
            type="button"
            className="btn-secondary"
            onClick={onRequestChange}
            disabled={changeMessage.trim().length === 0}
          >
            Send change request
          </button>
          {actionStatus ? <p className="action-status">{actionStatus}</p> : null}
        </div>
      ) : view.approvalState === "approved" ? (
        <p className="action-status">You approved this review plan.</p>
      ) : null}
    </section>
  );
}

export type { ClientPortalApiClient };
